import * as Ariakit from '@ariakit/react'
import classnames from 'classnames'
import * as React from 'react'

import { Box } from '../box'
import { Input, InputProps } from '../input'

export interface ComboBoxProps extends Partial<Omit<Ariakit.ComboboxProps, 'store' | 'onChange'>> {
  /**
   * The returned object from `useComboBoxState`
   */
  state: Ariakit.ComboboxStore

  /**
   * Controls when the popover is visible when passing items as children
   */
  hasData?: boolean

  /**
   * Additional props for the input
   */
  inputProps?: Partial<InputProps> & Omit<Partial<Ariakit.ComboboxProps>, 'size'>

  /**
   * Input element, in case of trigger with tooltip, etc.
   */
  inputComponent?: React.ReactElement

  /**
   * Additional props for the popover
   */
  popoverProps?: Partial<Ariakit.ComboboxPopoverProps>

  /**
   * Id for testing purposes
   */
  inputTestId?: string

  /**
   * Minimum num of chars in input to show the popover
   */
  minValueLength?: number

  /**
   * Inut value getter
   */
  getInputValue?: (activeValue: string | undefined, value: string) => string
}

export function ComboBox({
  state,
  children,
  hasData = true,
  inputProps,
  popoverProps,
  inputTestId,
  'aria-label': ariaLabel,
  minValueLength = 0,
  getInputValue,
  inputComponent,
  ...props
}: React.PropsWithChildren<ComboBoxProps>) {
  const isMounted = Ariakit.useStoreState(state, 'mounted')
  const value = Ariakit.useStoreState(state, 'value')
  const activeValue = Ariakit.useStoreState(state, 'activeValue')
  const placement = Ariakit.useStoreState(state, 'currentPlacement')

  const inputValue = React.useMemo(() => {
    if (getInputValue) return getInputValue(activeValue, value)

    return activeValue || value
  }, [getInputValue, value, activeValue])

  const commonProps = {
    ...inputProps,
    'aria-label': ariaLabel,
    'data-testid': inputTestId,
    onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Enter') {
        state.setSelectedValue(value)
      }
      inputProps?.onKeyDown?.(ev)
    },
  }

  return (
    <>
      <Ariakit.Combobox
        store={state}
        autoFocus={props.autoFocus ?? false}
        value={inputValue}
        render={inputComponent ? React.cloneElement(inputComponent, commonProps) : <Input {...commonProps} />}
        {...props}
      />
      {hasData && isMounted && value.length >= minValueLength && (
        <Ariakit.ComboboxPopover
          portal
          gutter={4}
          unmountOnHide
          render={<Box />}
          store={state}
          aria-label={ariaLabel}
          {...popoverProps}
          className={classnames(
            'z-dialog flex flex-col items-stretch space-y-px p-2 opacity-0 outline-none transition duration-[250ms] will-change-transform data-[enter]:translate-x-0 data-[enter]:translate-y-0 data-[enter]:opacity-100 motion-safe:transform motion-reduce:duration-[50ms]',
            {
              'motion-safe:translate-y-1': placement.startsWith('top'),
              'motion-safe:-translate-x-1': placement.startsWith('right'),
              'motion-safe:-translate-y-1': placement.startsWith('bottom'),
              'motion-safe:translate-x-1': placement.startsWith('left'),
            },
            popoverProps?.className
          )}
        >
          {children}
        </Ariakit.ComboboxPopover>
      )}
    </>
  )
}
